import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Table, Select, Typography, Space, Tag, Button, Popconfirm, notification } from 'antd';
import { StopOutlined } from '@ant-design/icons';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';
import { PdfButton, EmailButton } from '../../components/DocumentActions';
import { CREDIT_NOTE_STATUS_COLORS } from '../../components/CreditNotesCard';

import { date as fmtDate, label } from '../../lib/format';
const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Firm-wide credit note register; credit notes are raised from the invoice they correct. */
export default function CreditNotesPage() {
  const auth = useAuth();
  const [params] = useSearchParams();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const isManager = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const highlight = Number(params.get('highlight'));

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRows = () => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/credit-notes', { company_id: companyId, ...(statusFilter ? { status: statusFilter } : {}) })
      .then((r) => r.json())
      .then((r) => r.status && setRows(r.data))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchRows, [companyId, statusFilter]);

  const cancel = (note) => {
    request
      .post(`/credit-notes/${note.id}/cancel`, {})
      .then((r) => r.json())
      .then((r) => (r.status ? fetchRows() : notification.error({ message: r.message })));
  };

  const columns = [
    { title: 'Number', dataIndex: 'credit_note_number' },
    { title: 'Date', dataIndex: 'credit_date', sorter: (a, b) => (a.credit_date < b.credit_date ? -1 : 1), render: (v) => fmtDate(v) },
    { title: 'Client', key: 'client', render: (_, c) => (c.Client ? <Link to={`/clients/${c.Client.id}`}>{c.Client.client_name}</Link> : '-') },
    { title: 'Against invoice', key: 'inv', render: (_, c) => (c.Invoice ? <Link to={`/invoices/${c.Invoice.id}`}>{c.Invoice.invoice_number}</Link> : '-') },
    { title: 'Reason', dataIndex: 'reason' },
    { title: 'Total', dataIndex: 'total', align: 'right', render: money },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={CREDIT_NOTE_STATUS_COLORS[v]}>{label(v)}</Tag> },
    {
      title: '',
      key: 'actions',
      render: (_, c) => (
        <Space size={4}>
          <PdfButton type="credit-note" id={c.id} size="small" children={null} />
          {c.status === 'ISSUED' && <EmailButton type="credit-note" id={c.id} size="small" children={null} />}
          {c.status === 'ISSUED' && isManager && (
            <Popconfirm title="Cancel this credit note? The invoice balance is restored." onConfirm={() => cancel(c)}>
              <Button size="small" danger icon={<StopOutlined />} title="Cancel" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Credit Notes
          </Typography.Title>
          <Typography.Text type="secondary">Raised from the invoice page of the invoice they correct.</Typography.Text>
        </div>
        <Space>
          {isSystemAdmin && (
            <Select style={{ width: 200 }} value={companyId} placeholder="Company" onChange={setCompanyId} options={companies.map((c) => ({ value: c.id, label: c.name }))} />
          )}
          <Select allowClear style={{ width: 150 }} placeholder="All statuses" value={statusFilter} onChange={setStatusFilter} options={Object.keys(CREDIT_NOTE_STATUS_COLORS).map((s) => ({ value: s, label: s }))} />
        </Space>
      </Space>
      <Table rowKey="id" columns={columns} dataSource={rows} loading={loading} rowClassName={(c) => (c.id === highlight ? 'ant-table-row-selected' : '')} />
    </div>
  );
}
